/* Maison des Nus — OI mobile drawer controller (self-contained, no Dawn global.js dependency).
   Clones Orseund Iris menu-drawer behavior: left-slide panel + right-slide sub-panels
   (translate(100%)->0 via an .oi-submenu-opening class set on a rAF, mirroring Dawn's
   .menu-opening), scrim fade, MENU / X CLOSE label, Esc/scrim close, single open sub-panel. */
(function () {
  if (customElements.get('oi-menu-drawer')) return;

  class OiMenuDrawer extends HTMLElement {
    constructor() {
      super();
      this.wrapper = this.closest('.oi-header-wrapper');
      this.panel = this.querySelector('.oi-mobile-drawer__panel');
      this.scrim = this.querySelector('[data-drawer-scrim]');
      this.toggleBtn = this.wrapper && this.wrapper.querySelector('.oi-header__menu-toggle');
      this.detailsList = Array.prototype.slice.call(this.querySelectorAll('.oi-drawer-details'));
      this.onKeydown = this.onKeydown.bind(this);
    }

    connectedCallback() {
      // PORTAL OUT OF THE STICKY HEADER (iOS bug fix, 2026-07-31): the scrim + panel are
      // position:fixed, but this element ships inside the sticky header section. On real iOS
      // Safari, once the stuck/animating header gets its own compositing layer, fixed
      // descendants anchor to THAT ~55px-tall layer instead of the viewport — the drawer
      // "opens" (button flips to X CLOSE) but the panel computes zero visible area. Repro:
      // both client + Jared phones, any page, only when scrolled (at top the section isn't
      // composited). Fix: move the element to <body> so fixed means the viewport again.
      // (Constructor already captured this.wrapper/toggleBtn while still inside the header;
      // those references survive the move. The re-append re-fires connectedCallback — the
      // _portaled guard makes the first invocation move-only so listeners bind exactly once.)
      if (!this._portaled) {
        this._portaled = true;
        document.body.appendChild(this);
        return;
      }
      this.syncTopOffset();
      window.addEventListener('resize', this.syncTopOffset.bind(this), { passive: true });

      if (this.toggleBtn) {
        this.toggleBtn.setAttribute('aria-expanded', 'false');
        this.toggleBtn.addEventListener('click', (e) => { e.preventDefault(); this.toggle(); });
      }
      if (this.scrim) this.scrim.addEventListener('click', () => this.close());

      // SHOP / ABOUT sub-panels (right-slide)
      this.detailsList.forEach((details) => {
        const summary = details.querySelector('summary');
        const back = details.querySelector('.oi-mobile-drawer__close-button');
        if (summary) summary.addEventListener('click', (e) => { e.preventDefault(); this.openSubmenu(details); });
        if (back) back.addEventListener('click', (e) => { e.preventDefault(); this.closeSubmenu(details); });
      });
    }

    syncTopOffset() {
      // Position the drawer BELOW the visible top bar so OI's header (announcement + wordmark + BAG +
      // "X CLOSE") stays uncovered. Use the header's bounding BOTTOM (not just its height) so the
      // offset includes anything stacked above it (the announcement bar). Layout-only; no wiring.
      const header = this.wrapper && this.wrapper.querySelector('.oi-header');
      const h = header ? header.getBoundingClientRect().bottom : 60;
      this.style.setProperty('--oi-drawer-top', h + 'px');
      // viewport-height for iOS 100vh quirk
      document.documentElement.style.setProperty('--oi-viewport-height', window.innerHeight + 'px');
    }

    get isOpen() { return this.wrapper && this.wrapper.hasAttribute('data-drawer-open'); }

    toggle() { this.isOpen ? this.close() : this.open(); }

    open() {
      if (!this.wrapper) return;
      this.syncTopOffset();
      this.wrapper.setAttribute('data-drawer-open', '');
      this.setAttribute('data-drawer-open', ''); // panel/scrim CSS keys off the element itself since the body-portal
      // defensive: a lingering popup scroll-lock (touch-action:none) would eat every tap on iOS —
      // the popup and drawer can never be legitimately open together.
      document.documentElement.classList.remove('oi-nlp-lock');
      document.body.classList.remove('oi-nlp-lock');
      document.body.classList.add('overflow-hidden');
      if (this.toggleBtn) {
        this.toggleBtn.setAttribute('aria-expanded', 'true');
        this.toggleBtn.classList.add('is-open');
      }
      document.addEventListener('keydown', this.onKeydown);
      if (this.panel) {
        // focus after the transition starts
        requestAnimationFrame(() => this.panel.focus({ preventScroll: true }));
      }
    }

    close() {
      if (!this.wrapper) return;
      this.wrapper.removeAttribute('data-drawer-open');
      this.removeAttribute('data-drawer-open');
      document.body.classList.remove('overflow-hidden');
      if (this.toggleBtn) {
        this.toggleBtn.setAttribute('aria-expanded', 'false');
        this.toggleBtn.classList.remove('is-open');
      }
      document.removeEventListener('keydown', this.onKeydown);
      // reset any open sub-panels after the panel finishes sliding out
      window.setTimeout(() => this.detailsList.forEach((d) => this.closeSubmenu(d, true)), 250);
    }

    openSubmenu(details) {
      // close siblings first (single open sub-panel)
      this.detailsList.forEach((d) => { if (d !== details) this.closeSubmenu(d, true); });
      details.setAttribute('open', '');
      // mirror Dawn's .menu-opening: open attr first, then add the slide class on next frame
      requestAnimationFrame(() => details.classList.add('oi-submenu-opening'));
    }

    closeSubmenu(details, immediate) {
      if (!details.hasAttribute('open') && !details.classList.contains('oi-submenu-opening')) return;
      details.classList.remove('oi-submenu-opening');
      if (immediate) {
        details.removeAttribute('open');
      } else {
        // let it slide back out before removing [open]
        window.setTimeout(() => details.removeAttribute('open'), 200);
      }
    }

    onKeydown(e) {
      if (e.key !== 'Escape' && e.keyCode !== 27) return;
      // if a sub-panel is open, back out of it first; else close the drawer
      const openDetails = this.detailsList.find((d) => d.classList.contains('oi-submenu-opening'));
      if (openDetails) { this.closeSubmenu(openDetails); } else { this.close(); }
    }
  }

  customElements.define('oi-menu-drawer', OiMenuDrawer);
})();
